/**
 * Top bar (56px) plus a thin candy-stripe accent bar underneath it. Site
 * wordmark + store name/switcher on the left, theme toggle + avatar menu on
 * the right.
 *
 * The store switcher reuses the cockpit stores pages' useStoreMe/store-picker
 * pattern (see lib/useStores.ts, useStoreAssignment): a store account (has a
 * store_user mapping) sees their own store's name with no picker; a corporate
 * user (holds the stores.sweetspot grant, no store_user mapping) sees a
 * <select> of every active store and can view any of them. Both call the same
 * /v1/stores endpoints on the shared API.
 *
 * The sign-out modal is adapted from the cockpit UI's shell/Header.tsx
 * SignOutModal (same two-option pattern: local sign-out vs tenant-wide
 * Microsoft 365 sign-out).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bug, CircleHelp, LogOut, Moon, Settings, Sparkles, Sun } from 'lucide-react';
import { Modal } from '@ampersandbrands/ui';
import { useSession, getLogoutUrl } from '@/lib/useSession';
import logoUrl from '@/assets/logo.png';
import logoDarkUrl from '@/assets/logo-dark.png';
import { useTheme } from '@/lib/useTheme';
import { storeLabel, useEffectiveStoreNo, useStoreAssignment, useStoreList } from '@/lib/useStores';
import { useNewsletter } from '@/lib/useNewsletter';
import { useHqStore } from '@/lib/useHqStore';

function initialsFor(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return (parts[0] ?? '').slice(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
}

function StoreSwitcher() {
  const assignment = useStoreAssignment();
  const { setStoreNo: setHqStoreNo } = useHqStore();
  const listQ = useStoreList();

  if (assignment.kind === 'none' || assignment.kind === 'loading') return null;

  if (assignment.kind === 'corporate') {
    const stores = [...(listQ.data?.stores ?? [])].sort((a, b) => a.store_no - b.store_no);
    return (
      <select
        value={assignment.storeNo ?? ''}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && e.target.value !== '') setHqStoreNo(v);
        }}
        aria-label="Viewing store"
        className="inline-block max-w-[130px] truncate rounded-md border border-candy-line bg-candy-white px-2.5 py-1.5 text-[13px] text-candy-ink focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink sm:max-w-[220px]"
      >
        <option value="" disabled>
          {listQ.isLoading ? 'Loading stores...' : 'Select a store'}
        </option>
        {stores.map((s) => (
          <option key={s.store_no} value={s.store_no}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <span className="text-[13px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
      {storeLabel(assignment.storeNo, assignment.storeName)}
    </span>
  );
}

/**
 * This week's codeword, from the Unwrapped newsletter, on every page.
 *
 * Communications changes it weekly and prints it inside the newsletter
 * artwork; the importer reads it from the picture. Shown in the header so staff
 * have it whichever tool they are in. Shares the home page's newsletter query,
 * so it costs no extra request there. Renders nothing until a codeword exists.
 *
 * `variant` exists because the top bar has no room for it on a phone: there it
 * sits in a slim strip under the bar instead.
 */
function Codeword({ variant }: { variant: 'inline' | 'strip' }) {
  const storeNo = useEffectiveStoreNo();
  const { data: issue } = useNewsletter(storeNo);
  const codeword = issue?.codeword?.trim();
  if (!codeword) return null;

  if (variant === 'strip') {
    return (
      <div className="flex items-center justify-center gap-2 border-b border-candy-line bg-candy-cream px-4 py-1.5 dark:border-candy-dark-line dark:bg-candy-dark-surface-warm sm:hidden">
        <span className="text-[10px] font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
          This week&apos;s codeword
        </span>
        <span className="text-[13px] font-semibold tracking-wide text-candy-ink dark:text-candy-dark-ink">
          {codeword}
        </span>
      </div>
    );
  }

  return (
    <div
      className="hidden flex-col items-start rounded-md border border-candy-line bg-candy-cream px-2.5 py-1 leading-tight dark:border-candy-dark-line dark:bg-candy-dark-surface-warm sm:flex"
      title="This week's codeword, from the Unwrapped newsletter"
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
        This week&apos;s codeword
      </span>
      <span className="text-[13px] font-semibold tracking-wide text-candy-ink dark:text-candy-dark-ink">
        {codeword}
      </span>
    </div>
  );
}

function SignOutModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal
      title="Sign out"
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-3 py-1.5 text-sm text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
        >
          Cancel
        </button>
      }
    >
      <p className="mb-4 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Choose how far the sign-out should reach.
      </p>
      <div className="flex flex-col gap-2">
        <a
          href={getLogoutUrl(false)}
          className="block rounded-md border border-candy-line bg-candy-white px-3 py-2 text-left hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
        >
          <div className="text-sm font-medium text-candy-ink dark:text-candy-dark-ink">
            Sign out of The Sweet Spot
          </div>
          <div className="text-candy-ink-muted dark:text-candy-dark-ink-muted" style={{ fontSize: 11 }}>
            Stays signed into Outlook, Teams, Gmail, and other work apps.
          </div>
        </a>
        <a
          href={getLogoutUrl(true)}
          className="block rounded-md border border-candy-line bg-candy-white px-3 py-2 text-left hover:border-candy-pink hover:bg-candy-cream dark:border-candy-dark-line dark:bg-candy-dark-surface dark:hover:border-candy-pink-dark dark:hover:bg-candy-dark-surface-warm"
        >
          <div className="text-sm font-medium text-candy-ink dark:text-candy-dark-ink">
            Sign out of Microsoft 365
          </div>
          <div className="text-candy-ink-muted dark:text-candy-dark-ink-muted" style={{ fontSize: 11 }}>
            Signs you out of every Microsoft 365 app tenant-wide. Use on a shared workstation.
          </div>
        </a>
      </div>
    </Modal>
  );
}

function AvatarMenu() {
  const { user } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);

  if (!user) return null;
  const displayName = user.name?.trim() || user.email;
  const initials = initialsFor(user.name ?? '', user.email);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={menuOpen}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-candy-pink text-white dark:bg-candy-pink-dark dark:text-candy-dark-bg"
        style={{ fontSize: 12, fontWeight: 600 }}
      >
        {initials}
      </button>
      {menuOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-2 w-56 rounded-md border border-candy-line bg-candy-white p-1.5 shadow-2 dark:border-candy-dark-line dark:bg-candy-dark-surface">
            <div className="px-2.5 py-2">
              <p className="truncate text-sm font-medium text-candy-ink dark:text-candy-dark-ink">{displayName}</p>
              <p className="truncate text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">{user.email}</p>
            </div>
            <div className="my-1 h-px bg-candy-line dark:bg-candy-dark-line" />
            <Link
              to="/changelog"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-candy-ink hover:bg-candy-cream-warm dark:text-candy-dark-ink dark:hover:bg-candy-dark-surface-warm"
            >
              <Sparkles size={14} aria-hidden /> What&apos;s new
            </Link>
            <Link
              to="/settings"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-candy-ink hover:bg-candy-cream-warm dark:text-candy-dark-ink dark:hover:bg-candy-dark-surface-warm"
            >
              <Settings size={14} aria-hidden /> Settings
            </Link>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setSignOutOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-candy-ink hover:bg-candy-cream-warm dark:text-candy-dark-ink dark:hover:bg-candy-dark-surface-warm"
            >
              <LogOut size={14} aria-hidden /> Sign out
            </button>
          </div>
        </>
      )}
      {signOutOpen && <SignOutModal onClose={() => setSignOutOpen(false)} />}
    </div>
  );
}

export function Header({
  onHelpOpen,
  onReportIssueOpen,
}: {
  onHelpOpen?: () => void;
  onReportIssueOpen?: () => void;
} = {}) {
  const [theme, setTheme] = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="shrink-0">
      <header
        className="flex h-14 items-center justify-between border-b border-candy-line bg-candy-white dark:border-candy-dark-line dark:bg-candy-dark-surface"
        style={{ padding: '0 16px' }}
      >
        <Link
          to="/"
          className="flex items-center gap-2 text-candy-ink no-underline dark:text-candy-dark-ink"
        >
          <span className="inline-flex items-center">
            <img src={logoUrl} alt="The Sweet Spot" className="h-11 w-auto dark:hidden" />
            <img src={logoDarkUrl} alt="" aria-hidden className="hidden h-9 w-auto dark:block" />
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Codeword variant="inline" />
          <StoreSwitcher />
          {onHelpOpen && (
            <button
              type="button"
              onClick={onHelpOpen}
              aria-label="Open help"
              title="Help"
              className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-candy-ink-muted hover:bg-candy-cream dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
            >
              <CircleHelp size={16} aria-hidden />
            </button>
          )}
          {onReportIssueOpen && (
            <button
              type="button"
              onClick={onReportIssueOpen}
              aria-label="Report an issue"
              title="Report an issue"
              className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-candy-ink-muted hover:bg-candy-cream dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
            >
              <Bug size={16} aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={isDark}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-candy-ink-muted hover:bg-candy-cream dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
          >
            {isDark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
          </button>
          <AvatarMenu />
        </div>
      </header>
      <Codeword variant="strip" />
      <div className="candy-stripe" aria-hidden />
    </div>
  );
}
